// src/shared/lib/local-date.ts
// LOCAL-calendar date ↔ string conversions for date fields (PROD-1500 date-picker sweep).
//
// ⚠️ The whole point of this module is avoiding the UTC off-by-one class:
//   - `new Date('2026-07-07')` (bare YYYY-MM-DD) parses as UTC MIDNIGHT → renders as
//     "Jul 6" in any UTC+ zone.
//   - `date.toISOString().slice(0, 10)` converts a LOCAL midnight to UTC first → writes
//     "yesterday" for early-morning +tz users (bit us on the PROD-1489 Resolve date).
// Every date-only field must round-trip through THESE helpers, never the two patterns above.

/**
 * `YYYY-MM-DD` (bare, or the leading date of an ISO datetime — `T` continuation only, not
 * arbitrary trailing garbage) → Date at LOCAL midnight. Empty / malformed / impossible
 * calendar dates → undefined: `new Date(y, m, d)` silently rolls overflow (2026-02-31 →
 * Mar 3), so the round-trip is verified instead of trusting the constructor (Codex review).
 */
export function ymdToLocalDate(ymd: string | null | undefined): Date | undefined {
  if (!ymd) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(ymd);
  if (!m) return undefined;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(year, month - 1, day);
  const real = d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
  return real ? d : undefined;
}

/** Date → `YYYY-MM-DD` in the LOCAL calendar. undefined → ''. */
export function dateToYmd(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * ISO datetime or bare date → LOCAL `YYYY-MM-DD`.
 * A leading `YYYY-MM-DD` is taken as the CALENDAR day verbatim — this makes bare dates
 * (e.g. the inventory lot `expiryDate: "2026-08-21"`) timezone-proof: `new Date('2026-08-21')`
 * parses UTC midnight and reads as the PREVIOUS day in UTC− zones. PA's datetime wire is
 * no-Z ISO (local), so the leading date is the local day there too. Anything else falls back
 * to Date parsing.
 */
export function isoToYmdLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const viaCalendar = ymdToLocalDate(iso);
  if (viaCalendar) return dateToYmd(viaCalendar);
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : dateToYmd(d);
}

/** `YYYY-MM-DD` → no-Z ISO local midnight (`YYYY-MM-DDT00:00:00.000`) — the .NET wire shape
 *  (model binder rejects a Z suffix). Empty → null. */
export function ymdToNoZIso(ymd: string | null | undefined): string | null {
  if (!ymd) return null;
  return `${ymd}T00:00:00.000`;
}
